import type { ActiveGameData } from '@ps/web-poker-types';
import { ActiveGameButtonType, ActiveGameType } from '@ps/web-poker-types';
import { StarsButton as Button, Translation } from '@ps/web-shared-components';
import type React from 'react';
import { useCallback, useState } from 'react';

import { TXTCLI_Take_Your_Seat, TXTCLI_Unregister } from '../../translations';

import ConfirmButton from './ConfirmButton';
import styles from './GameItem.module.scss';
import GameItemInfoCash from './GameItemInfoCash';
import GameItemInfoSAG from './GameItemInfoSAG';
import GameItemInfoTourney from './GameItemInfoTourney';
import { GAME_TYPE_TRANSLATIONS } from './utils/utils';

interface Props {
  game: ActiveGameData;
  confirmAction?: boolean;
  onAction?: (game: ActiveGameData) => void;
}

const GameItem: React.FC<Props> = ({
  game,
  onAction,
  confirmAction = game.buttonType === ActiveGameButtonType.UNREGISTER
}) => {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleClick = useCallback(() => {
    if (!confirmAction) {
      return onAction?.(game);
    }
    setShowConfirm(true);
  }, [confirmAction, game, onAction]);

  const handleCancel = useCallback(() => {
    setShowConfirm(false);
  }, []);

  const handleConfirm = useCallback(() => {
    setShowConfirm(false);
    onAction?.(game);
  }, [game, onAction]);

  return (
    <div className={styles.wrap}>
      <div className={styles.titleWrap}>
        <p className={styles.type}>
          <Translation {...GAME_TYPE_TRANSLATIONS[game.type]} />
        </p>
        <p className={styles.title}>
          {game.type === ActiveGameType.Tourney && <GameItemInfoTourney game={game} />}
          {game.type === ActiveGameType.Cash && <GameItemInfoCash game={game} />}
          {game.type === ActiveGameType.SAG && <GameItemInfoSAG game={game} />}
        </p>
      </div>
      <div className={styles.buttonsWrap}>
        {!showConfirm && game.buttonType === ActiveGameButtonType.UNREGISTER && (
          <Button
            className={styles.unregister}
            variant="primary"
            translation={TXTCLI_Unregister}
            size="small"
            onClick={handleClick}
          />
        )}
        {!showConfirm && game.buttonType === ActiveGameButtonType.TAKE_SEAT && (
          <Button
            variant="primary"
            translation={TXTCLI_Take_Your_Seat}
            size="small"
            onClick={handleClick}
          />
        )}
        {showConfirm && <ConfirmButton onCancel={handleCancel} onConfirm={handleConfirm} />}
      </div>
    </div>
  );
};

export default GameItem;
